import apiClient from '../config/api';

// Client-side cache for user email -> name mappings
class UserNameService {
    constructor() {
        this.cache = new Map();
    }

    // Get user name from cache
    getCached(email) {
        return this.cache.get(email);
    }

    // Check if email is in cache
    hasCached(email) {
        return this.cache.has(email);
    }

    // Fetch user names from server and update cache
    async fetchUserNames(emails) {
        if (!emails || emails.length === 0) {
            return {};
        }

        // Filter out emails that are already in cache
        const uncachedEmails = emails.filter(email => !this.hasCached(email));

        // If all emails are cached, return from cache
        if (uncachedEmails.length === 0) {
            const result = {};
            emails.forEach(email => {
                result[email] = this.getCached(email);
            });
            return result;
        }

        try {
            // Fetch names for uncached emails
            const response = await apiClient.post('/api/auth/user-names', {
                emails: uncachedEmails
            });

            const names = response.data.names;

            // Update cache with new names
            Object.entries(names).forEach(([email, name]) => {
                this.cache.set(email, name);
            });

            // Return all requested names (from cache + newly fetched)
            const result = {};
            emails.forEach(email => {
                result[email] = this.getCached(email) || email;
            });

            return result;
        } catch (error) {
            console.error('Error fetching user names:', error);
            // Return emails as fallback
            const result = {};
            emails.forEach(email => {
                result[email] = this.getCached(email) || email;
            });
            return result;
        }
    }

    // Get a single user name
    async getUserName(email) {
        if (!email) return email;
        
        if (this.hasCached(email)) {
            return this.getCached(email);
        }

        const names = await this.fetchUserNames([email]);
        return names[email] || email;
    }

    // Clear the cache
    clearCache() {
        this.cache.clear();
    }
}

// Export a singleton instance
export default new UserNameService();

